//src/pages/pricing/PricingPage.js

import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, getUserUsageStats } from '../../context/AuthContext';
import axios from 'axios';
import './Pricing.css';
import { logger } from '../../utils/logger';

// Components
import PlanCard from './components/PlanCard';
import CustomPlanConfig from './components/CustomPlanConfig';
import PaymentModal from './components/PaymentModal';
import CurrentPlanSummary from './components/CurrentPlanSummary';
import SuccessNotification from './components/SuccessNotification';

// Utilities
import { formatNumber, calculateCustomPrice, validateSquareEnvironment } from './utils/pricingUtils';

// Constants
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Minimum and maximum values for custom plan
const MIN_AGENTS = 1;
const MAX_AGENTS = 100;
const MIN_QUERIES = 50;
const MAX_QUERIES = 10000;
const MIN_DOCUMENTS = 10;
const MAX_DOCUMENTS = 50000;

const PricingPage = () => {
  const { 
    user, 
    subscriptionStatus, 
    refreshSubscriptionStatus,
    debouncedRefresh,
    cancelSubscription,
    session
  } = useContext(AuthContext);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Debug log subscription status
    console.log("Current subscription status:", subscriptionStatus);
  }, [subscriptionStatus]);
  
  // Get current plan details from subscription status
  const currentPlan = subscriptionStatus?.plan || 'Free';
  const currentAgents = subscriptionStatus?.limits?.agentLimit || 1;
  const currentQueries = subscriptionStatus?.limits?.queryLimit || 10;
  const currentDocuments = subscriptionStatus?.limits?.documentLimit || 5;
  const isCurrentActive = subscriptionStatus?.active || false;
  
  // Trial specific information
  const trialEnded = subscriptionStatus?.trialEnded || false;
  const trialActive = subscriptionStatus?.trialActive || false;
  const trialEndDate = subscriptionStatus?.trialEndDate ? new Date(subscriptionStatus.trialEndDate) : null;
  
  // Calculate days left in trial if active
  const daysLeftInTrial = useMemo(() => {
    if (!trialActive || !trialEndDate) return 0;
    
    const now = new Date();
    const diff = trialEndDate.getTime() - now.getTime();
    const daysDiff = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return Math.max(0, daysDiff);
  }, [trialActive, trialEndDate]);
  
  // State for success message
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // State for custom plan sliders
  const [agentCount, setAgentCount] = useState(1);
  const [queryCount, setQueryCount] = useState(50);
  const [documentCount, setDocumentCount] = useState(10);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [showCustomConfig, setShowCustomConfig] = useState(false);
  
  // State for payment processing
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [squareStatus, setSquareStatus] = useState('loading');
  const [cardContainerKey, setCardContainerKey] = useState(0);
  const [formValidation, setFormValidation] = useState({
    isValid: true,
    agentError: '',
    queryError: '',
    documentError: ''
  });
  
  // Calculate custom price
  const customPrice = useMemo(() => 
    calculateCustomPrice(agentCount, queryCount, documentCount), 
    [agentCount, queryCount, documentCount]
  );
  
  // Define plan tiers with useMemo to prevent unnecessary recalculations
  const plans = useMemo(() => [
    {
      id: 'free',
      name: 'Free',
      description: 'Try Advantis AI for free.',
      price: 0,
      features: [
        '1 Agent',
        '10 Queries per month',
        '5 Documents',
        'Expires after 2 days'
      ]
    },
    {
      id: 'custom',
      name: 'Custom',
      description: 'Tailored to your needs.',
      price: customPrice,
      features: [
        `${agentCount} Agent${agentCount > 1 ? 's' : ''}`,
        `${formatNumber(queryCount)} Queries per month`,
        `${formatNumber(documentCount)} Documents`,
        'Click Now to Customize'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'For organizations with advanced needs.',
      price: null,
      features: [
        'Unlimited Agents',
        'Unlimited Queries',
        'Unlimited Documents',
        'Custom Integrations',
        'Live Data Syncing',
      ]
    }
  ], [agentCount, queryCount, documentCount, customPrice]);

  // Effect to reset payment errors when modal is closed
  useEffect(() => {
    if (!showPaymentModal) {
      setPaymentError(null);
      setSquareStatus('loading');
    }
  }, [showPaymentModal]);

  // Use refreshSubscriptionStatus instead of debouncedRefresh
  useEffect(() => {
    if (session?.access_token) {
      // Initial refresh on component mount
      refreshSubscriptionStatus(session.access_token, true);
    }
  }, [session, refreshSubscriptionStatus]);

  // State for usage statistics
  const [usageStats, setUsageStats] = useState(null);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  
  // Fetch real-time usage statistics when the component mounts or subscription status changes
  useEffect(() => {
    const fetchUsageStats = async () => {
      if (!session) return;
      
      setIsLoadingUsage(true);
      try {
        const stats = await getUserUsageStats(session);
        if (stats) {
          setUsageStats(stats);
          console.log('Fetched usage stats:', stats);
        }
      } catch (error) {
        console.error('Error fetching usage statistics:', error);
      } finally {
        setIsLoadingUsage(false);
      }
    };
    
    // Fetch immediately
    fetchUsageStats();
    
  }, [session, subscriptionStatus]);

  // Validate custom plan inputs
  const validateCustomPlan = () => {
    const errors = {
      isValid: true,
      agentError: '',
      queryError: '',
      documentError: ''
    };
    
    if (agentCount < MIN_AGENTS || agentCount > MAX_AGENTS) {
      errors.isValid = false;
      errors.agentError = `Agent count must be between ${MIN_AGENTS} and ${MAX_AGENTS}`;
    }
    
    if (queryCount < MIN_QUERIES || queryCount > MAX_QUERIES) {
      errors.isValid = false;
      errors.queryError = `Query count must be between ${formatNumber(MIN_QUERIES)} and ${formatNumber(MAX_QUERIES)}`;
    }
    
    if (documentCount < MIN_DOCUMENTS || documentCount > MAX_DOCUMENTS) {
      errors.isValid = false;
      errors.documentError = `Document count must be between ${formatNumber(MIN_DOCUMENTS)} and ${formatNumber(MAX_DOCUMENTS)}`;
    }
    
    setFormValidation(errors);
    return errors.isValid;
  };
  
  // Process the payment with Square
  const processPayment = async (squareCard) => {
    if (!user) {
      navigate('/login', { state: { returnTo: '/pricing' } });
      return;
    }
    
    setIsProcessing(true);
    setPaymentError(null);
    
    try {
      // Check if this is an upgrade from free trial
      const isUpgradeFromFreeTrial = trialActive || 
        (subscriptionStatus?.status === 'ACTIVE' && subscriptionStatus?.plan === 'Free');
      
      // Check if this is an upgrade or change to an existing custom plan
      const isCustomPlanUpgrade = isCurrentActive && currentPlan.toLowerCase() === 'custom';
      
      // Combine flags to determine if this is any type of upgrade
      const isUpgrade = isCustomPlanUpgrade || isUpgradeFromFreeTrial;

      // Log the upgrade status for debugging
      console.log("Plan change details:", {
        isUpgradeFromFreeTrial,
        isCustomPlanUpgrade,
        isUpgrade,
        currentPlan,
        isCurrentActive,
        trialActive
      });
      
      // Metadata for tracking
      const metadata = {
        user_id: user.id,
        user_email: user.email,
        plan_id: selectedPlanDetails.id,
        plan_name: selectedPlanDetails.name,
        plan_price: selectedPlanDetails.id === 'custom' ? customPrice : selectedPlanDetails.price,
        created_at: new Date().toISOString(),
        is_upgrade: isUpgrade
      };
      
      // Add custom plan configuration
      if (selectedPlanDetails.id === 'custom') {
        metadata.custom_config = {
          agent_count: agentCount,
          query_count: queryCount,
          document_count: documentCount
        };
      }
      
      // Get or create Square customer
      let customerId;
      try {
        const customerResponse = await axios.post(
          `${API_BASE_URL}/api/pricing/payment/customer`,
          {
            email: user.email,
            firstName: user.user_metadata?.first_name || '',
            lastName: user.user_metadata?.last_name || ''
          },
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`
            }
          }
        );
        customerId = customerResponse.data.customerId;
        logger.debug(`Using customer ID: ${customerId}`);
      } catch (error) {
        throw new Error('Failed to set up customer profile for subscription');
      }
      
      // Log initial state for debugging
      logger.info('Starting payment processing with state:', {
        isUpgradeFromFreeTrial,
        isCustomPlanUpgrade,
        isUpgrade,
        currentPlan,
        selectedPlan: selectedPlanDetails.id,
        price: selectedPlanDetails.id === 'custom' ? customPrice : selectedPlanDetails.price
      });
      
      // For custom plans, tokenize the card
      if (selectedPlanDetails.id === 'custom') {
        logger.info('Tokenizing card for custom plan...');
        const tokenResult = await squareCard.tokenize();
        if (tokenResult.status !== 'OK') {
          logger.error('Card tokenization failed:', tokenResult);
          setPaymentError('Failed to process card. Please check your card details and try again.');
          setIsProcessing(false);
          return;
        }
        
        const cardToken = tokenResult.token;
        logger.info(`Card tokenized successfully with token: ${cardToken.substring(0, 6)}... (length: ${cardToken.length})`);
        
        // Add more specific upgrade information to metadata
        if (isUpgradeFromFreeTrial) {
          metadata.upgraded_from_free_trial = true;
          metadata.free_trial_active = trialActive;
          metadata.subscription_id = subscriptionStatus?.id;
          
          // Add additional metadata specific to free trial upgrade
          metadata.is_upgrade_from_free_trial = true;
          
          // Include trial status information
          if (subscriptionStatus) {
            metadata.trial_status = subscriptionStatus.status;
            metadata.trial_end_date = subscriptionStatus.trialEndDate;
          }
          
          logger.info('Including free trial upgrade metadata:', {
            trialId: subscriptionStatus?.id,
            trialActive,
            upgradeType: 'free_trial_to_custom'
          });
        } else if (isCustomPlanUpgrade) {
          metadata.upgraded_from_custom_plan = true;
          metadata.subscription_id = subscriptionStatus?.id;
          
          logger.info('Including custom plan upgrade metadata:', {
            subscriptionId: subscriptionStatus?.id,
            upgradeType: 'custom_to_custom'
          });
        }
        
        // Create the subscription with error logging
        logger.info('Calling subscription API with metadata:', metadata);
        try {
          const response = await axios.post(
            `${API_BASE_URL}/api/pricing/subscribe`,
            {
              plan_id: 'custom',
              source_id: cardToken,
              customer_id: customerId,
              metadata
            },
            {
              headers: {
                Authorization: `Bearer ${session.access_token}`
              }
            }
          );
          
          // Use appropriate message based on whether this is an upgrade
          const successMessage = isUpgrade 
            ? isUpgradeFromFreeTrial 
              ? 'Successfully upgraded from free trial to custom plan!'
              : 'Your custom plan has been successfully updated!'
            : 'Custom subscription created successfully!';
          
          handleSubscriptionSuccess(response, successMessage);
        } catch (apiError) {
          logger.error('Subscription API error:', apiError);
          // Extract detailed error information if available
          if (apiError.response && apiError.response.data) {
            logger.error('API error details:', apiError.response.data);
          }
          throw apiError; // Re-throw to be handled by the catch block
        }
      } else if (selectedPlanDetails.id === 'enterprise') {
        // For enterprise, just redirect to sales
        window.location.href = 'mailto:info@advantissolutions.com?subject=Enterprise Plan Inquiry';
        setIsProcessing(false);
        setShowPaymentModal(false);
      }
    } catch (error) {
      handlePaymentError(error);
    }
  };
  
  // Activate free trial (without payment modal)
  const activateFreeTrial = async () => {
    if (!user) {
      navigate('/login', { state: { returnTo: '/pricing' } });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Metadata for tracking
      const metadata = {
        user_id: user.id,
        user_email: user.email,
        plan_id: 'free',
        plan_name: 'Free',
        plan_price: 0,
        created_at: new Date().toISOString()
      };
      
      // Activate free trial directly
      const response = await axios.post(
        `${API_BASE_URL}/api/pricing/subscribe`,
        {
          plan_id: 'free',
          source_id: 'free_trial',
          customer_id: null,
          metadata
        },
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        }
      );
      
      if (response.data?.status === 'success') {
        // Show success message
        setSuccessMessage('Free trial activated successfully!');
        setShowSuccess(true);
        
        // Refresh subscription status with force refresh
        refreshSubscriptionStatus(session.access_token, true);
        
        setTimeout(() => {
          setShowSuccess(false);
          navigate('/dashboard');
        }, 1000);
      } else {
        throw new Error(response.data?.message || 'Failed to activate free trial');
      }
    } catch (error) {
      let errorMessage = 'Failed to activate free trial. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setPaymentError(errorMessage);
      logger.error('Free trial activation error:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Handle subscription success
  const handleSubscriptionSuccess = (response, message) => {
    if (!response.data || response.data?.status !== 'success') {
      throw new Error(response.data?.message || 'Failed to create subscription');
    }
    
    setIsProcessing(false);
    setShowPaymentModal(false);
    
    // Refresh subscription status with force refresh
    refreshSubscriptionStatus(session.access_token, true);
    
    // Show success message
    setSuccessMessage(message);
    setShowSuccess(true);
    
    setTimeout(() => {
      setShowSuccess(false);
      navigate('/dashboard');
    }, 1000);
  };
  
  // Handle payment errors
  const handlePaymentError = (error) => {
    setIsProcessing(false);
    let errorMessage = 'Payment processing failed. Please try again later.';
    
    logger.error('Payment error:', error);
    
    if (error.response && error.response.data) {
      const responseData = error.response.data;
      
      // Log detailed error information for debugging
      console.error('Payment error details:', {
        status: error.response.status,
        data: responseData,
        headers: error.response.headers
      });
      
      // Try to extract the most useful error message
      if (responseData.message) {
        if (responseData.message.includes('Square API Error')) {
          // This is a Square API error
          errorMessage = responseData.message;
        } else if (responseData.message.includes('Bad Request')) {
          errorMessage = 'There was an issue with the payment information. Please check your card details and try again.';
        } else {
          errorMessage = responseData.message;
        }
      }
      
      // Specific handling for known error conditions
      if (responseData.code === 'CARD_DECLINED' || 
          (responseData.message && responseData.message.toLowerCase().includes('declined'))) {
        errorMessage = 'Your card was declined. Please try a different payment method.';
      } else if (responseData.code === 'INVALID_CARD' || 
                (responseData.message && responseData.message.toLowerCase().includes('invalid card'))) {
        errorMessage = 'The card information you provided is invalid. Please check your card details.';
      } else if (responseData.message && responseData.message.includes('cannot be accessed by customer')) {
        errorMessage = 'There was an error processing your card. Please try again with a different card.';
      } else if (responseData.code === 'INSUFFICIENT_FUNDS') {
        errorMessage = 'Your card has insufficient funds. Please try a different payment method.';
      } else if (responseData.message && responseData.message.includes('already have an active subscription')) {
        errorMessage = 'You already have an active subscription. Please cancel your current subscription before creating a new one.';
      } else if (responseData.message && responseData.message.includes('already used your free trial')) {
        errorMessage = 'You have already used your free trial. Please select a paid plan.';
      } else if (responseData.message && responseData.message.includes('Failed to create customer card')) {
        errorMessage = 'We couldn\'t process your card. Please try again with a different card or check your card details.';
      }
    } else if (error.message) {
      // For other error types
      errorMessage = error.message;
    }
    
    // Set the error message for display
    setPaymentError(errorMessage);
  };

  // Handlers for slider changes
  const handleAgentChange = (e) => {
    const value = parseInt(e.target.value);
    setAgentCount(value);
    validateCustomPlan();
    updateCustomPlan();
  };
  
  const handleQueryChange = (e) => {
    const value = parseInt(e.target.value);
    setQueryCount(value);
    validateCustomPlan();
    updateCustomPlan();
  };
  
  const handleDocumentChange = (e) => {
    const value = parseInt(e.target.value);
    setDocumentCount(value);
    validateCustomPlan();
    updateCustomPlan();
  };
  
  // Update custom plan automatically
  const updateCustomPlan = () => {
    const customPlanInPlansList = plans.find(p => p.id === 'custom');
    if (customPlanInPlansList && formValidation.isValid) {
      setSelectedPlan('custom');
    }
  };
  
  // Handle plan selection
  const selectPlan = (planId) => {
    setSelectedPlan(planId);
    if (planId === 'custom') {
      setShowCustomConfig(true);
    }
  };
  
  // Handle purchase plan action
  const handlePurchasePlan = (plan) => {
    if (!user) {
      navigate('/login', { state: { returnTo: '/pricing' } });
      return;
    }
    
    if (plan.id === 'enterprise') {
      window.location.href = 'mailto:info@advantissolutions.com?subject=Enterprise Plan Inquiry';
      return;
    }
    
    // Special handling for free plan - direct activation without modal
    if (plan.id === 'free') {
      activateFreeTrial();
      return;
    }
    
    // For custom plan, show payment modal with Square
    if (plan.id === 'custom') {
      // Validate Square environment before opening payment modal
      const squareValidation = validateSquareEnvironment();
      if (!squareValidation.isValid) {
        logger.error('Square validation failed:', squareValidation.message);
        setPaymentError(squareValidation.message);
        setSquareStatus('error');
      } else {
        logger.debug('Square environment validated successfully', squareValidation);
        setPaymentError(null);
        setSquareStatus('loading');
      }
      
      // Force a remount of the card container
      setCardContainerKey(prev => prev + 1);
      setSelectedPlanDetails(plan);
      setShowPaymentModal(true);
    }
  };
  
  // Helper function to determine button text based on plan type and current status
  const getButtonText = (plan) => {
    if (plan.id === 'free') {
      // New debugging to help track what's happening
      console.log('Free plan button status:', { 
        trialActive, 
        trialEnded, 
        daysLeftInTrial,
        trialEndDate: trialEndDate ? trialEndDate.toISOString() : null
      });
      
      if (trialActive) {
        return `Active Trial (${daysLeftInTrial} day${daysLeftInTrial !== 1 ? 's' : ''} left)`;
      } else if (trialEnded) {
        return 'Trial Used';
      } else {
        return 'Start Free Trial';
      }
    }
    
    // For custom plan, always allow changing
    if (plan.id === 'custom') {
      if (isCurrentPlan(plan.id) && isCurrentActive) {
        return 'Update Plan';
      }
      return 'Pay Now';
    }
    
    if (isCurrentPlan(plan.id) && isCurrentActive) {
      return 'Current Plan';
    }
    
    switch (plan.id) {
      case 'enterprise':
        return 'Book Now';
      default:
        return 'Subscribe Now';
    }
  };
  
  // Check if a plan is the current plan (case insensitive comparison)
  const isCurrentPlan = (planId) => {
    return currentPlan.toLowerCase() === planId.toLowerCase();
  };
  
  // Determine if the button should be disabled
  const isButtonDisabled = (plan) => {
    // Added debugging
    if (plan.id === 'free') {
      console.log('Button disabled state for free plan:', {
        trialActive,
        trialEnded,
        isCurrentPlan: isCurrentPlan(plan.id),
        isCurrentActive
      });
    }
    
    // Only disable free trial button if the trial is active, has ended, or it's the current active plan
    if (plan.id === 'free') {
      return trialActive || trialEnded || (isCurrentPlan(plan.id) && isCurrentActive);
    } 
    // Never disable custom plan - users should always be able to update
    else if (plan.id === 'custom') {
      return false;
    }
    // Disable other plans if they're the current active plan
    else if (isCurrentPlan(plan.id) && isCurrentActive) {
      return true;
    }
    
    return isProcessing;
  };
  
  // State for subscription cancellation
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    if (!subscriptionStatus?.id) {
      setSuccessMessage('No active subscription to cancel.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      return;
    }
    
    setIsCancelling(true);
    
    try {
      const result = await cancelSubscription();
      
      if (result) {
        // Show success message
        setSuccessMessage('Your subscription has been cancelled successfully.');
        setShowSuccess(true);
        
        // Force refresh subscription status
        await refreshSubscriptionStatus(session.access_token, true);
        
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      } else {
        throw new Error('Failed to cancel subscription');
      }
    } catch (error) {
      logger.error('Error cancelling subscription:', error);
      setPaymentError(error.response?.data?.message || 'Failed to cancel subscription. Please try again later.');
      
      setTimeout(() => {
        setPaymentError(null);
      }, 5000);
    } finally {
      setIsCancelling(false);
    }
  };
  
  return (
    <div className="pricing-page">
      {showSuccess && (
        <SuccessNotification 
          message={successMessage}
          onClose={() => setShowSuccess(false)}
        />
      )}
      
      <CurrentPlanSummary 
        currentPlan={currentPlan}
        isCurrentActive={isCurrentActive}
        currentAgents={usageStats?.agents?.current || currentAgents}
        currentQueries={usageStats?.queries?.current || currentQueries}
        currentDocuments={usageStats?.documents?.current || currentDocuments}
        agentLimit={usageStats?.agents?.limit || currentAgents}
        queryLimit={usageStats?.queries?.limit || currentQueries}
        documentLimit={usageStats?.documents?.limit || currentDocuments}
        isLoading={isLoadingUsage}
        onCancelSubscription={handleCancelSubscription}
        subscriptionId={subscriptionStatus?.id}
        isCancelling={isCancelling}
      />
      
      <div className="pricing-header">
        <h1>Choose the Right Plan for You</h1>
        <p>Select a plan that best fits your needs and scale as you grow.</p>
      </div>
      
      {paymentError && (
        <div className="payment-error-banner">
          <p>{paymentError}</p>
          <button onClick={() => setPaymentError(null)}>✕</button>
        </div>
      )}
      
      <div className="pricing-cards-container">
        <div className="pricing-cards">
          {plans.map(plan => (
            <PlanCard 
              key={plan.id}
              plan={plan}
              isSelected={selectedPlan === plan.id}
              isCurrentPlan={isCurrentPlan(plan.id) && isCurrentActive}
              isTrialUsed={plan.id === 'free' && trialEnded}
              isCurrentActive={isCurrentActive}
              buttonText={getButtonText(plan)}
              onSelect={() => plan.id === 'custom' ? setShowCustomConfig(true) : selectPlan(plan.id)}
              onPurchase={() => handlePurchasePlan(plan)}
              isButtonDisabled={isButtonDisabled(plan)}
            />
          ))}
        </div>
      </div>
      
      {showPaymentModal && (
        <PaymentModal
          isProcessing={isProcessing}
          selectedPlanDetails={selectedPlanDetails}
          agentCount={agentCount}
          queryCount={queryCount}
          documentCount={documentCount}
          squareStatus={squareStatus}
          paymentError={paymentError}
          onClose={() => {
            setShowPaymentModal(false);
            // Increment card container key when modal closes to ensure fresh instance on reopen
            setCardContainerKey(prev => prev + 1);
          }}
          onProcessPayment={processPayment}
          isCurrentPlan={isCurrentPlan}
          isCurrentActive={isCurrentActive}
          cardContainerKey={cardContainerKey}
        />
      )}
      
      {showCustomConfig && (
        <CustomPlanConfig
          agentCount={agentCount}
          queryCount={queryCount}
          documentCount={documentCount}
          customPrice={customPrice}
          formValidation={formValidation}
          minAgents={MIN_AGENTS}
          maxAgents={MAX_AGENTS}
          minQueries={MIN_QUERIES}
          maxQueries={MAX_QUERIES}
          minDocuments={MIN_DOCUMENTS}
          maxDocuments={MAX_DOCUMENTS}
          onAgentChange={handleAgentChange}
          onQueryChange={handleQueryChange}
          onDocumentChange={handleDocumentChange}
          onClose={() => setShowCustomConfig(false)}
        />
      )}
    </div>
  );
};

export default PricingPage; 